import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { money, PLACEHOLDER } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import CreateStudioDialog from "@/components/CreateStudioDialog";
import WithdrawDialog from "@/components/WithdrawDialog";
import AdminPanel from "@/components/AdminPanel";
import ProfileDetails from "@/components/ProfileDetails";
import { toast } from "sonner";
import { Wallet, Plus, Trash2, Pencil, Music4, Building2, ShieldCheck } from "lucide-react";

const vstatus = {
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  rejected: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function ProfileTab() {
  const { user } = useAuth();
  const { t } = useLang();
  const [wallet, setWallet] = useState(null);
  const [studios, setStudios] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [ownerBookings, setOwnerBookings] = useState([]);
  const [editStudio, setEditStudio] = useState(null);
  const [income, setIncome] = useState(null);

  const isOwner = user?.role === "owner";
  const isAdmin = user?.is_admin;

  const load = useCallback(async () => {
    if (user?.role === "owner") {
      const [w, s, b, inc] = await Promise.all([api.get("/wallet"), api.get("/studios/mine"), api.get("/bookings/owner"), api.get("/owner/income")]);
      setWallet(w.data);
      setStudios(s.data.studios);
      setOwnerBookings(b.data.bookings);
      setIncome(inc.data);
    }
  }, [user?.role]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    await api.delete(`/studios/${id}`);
    toast.success(t("saved"));
    load();
  };

  const collect = async (id) => {
    await api.post(`/bookings/${id}/collect`);
    toast.success(t("collected"));
    load();
  };

  return (
    <div className="ss-rise max-w-3xl">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-5">{t("profile_wallet")}</h1>

      {/* Identity */}
      <div className="flex items-center gap-4 p-4 rounded-2xl border border-border bg-card mb-5">
        <Avatar className="w-14 h-14 border border-border">
          <AvatarImage src={user?.picture} />
          <AvatarFallback className="bg-secondary">{user?.name?.[0]?.toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <p className="font-display font-bold text-lg">{user?.name}</p>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
        </div>
        {user?.is_admin && <Badge className="bg-primary/20 text-primary border-primary/30 gap-1"><ShieldCheck className="w-3 h-3" /> Admin</Badge>}
      </div>

      {!isAdmin && <ProfileDetails />}

      {/* Account type (fixed at signup) */}
      {!isAdmin && (
        <div className="p-4 rounded-2xl border border-border bg-card mb-5 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("account_type")}</p>
          <Badge className="bg-primary/20 text-primary border-primary/30 gap-1">
            {isOwner ? <Building2 className="w-3 h-3" /> : <Music4 className="w-3 h-3" />} {t(user?.role || "musician")}
          </Badge>
        </div>
      )}

      {isOwner && (
        <>
          {/* Wallet */}
          <div className="p-5 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent mb-5">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2"><Wallet className="w-4 h-4" /> {t("wallet")}</div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xs text-muted-foreground">{t("available_balance")}</p>
                <p data-testid="wallet-available" className="font-display text-4xl font-extrabold text-primary">{money(wallet?.available || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("pending_balance")}: {money(wallet?.pending || 0)}</p>
              </div>
              <Button data-testid="open-withdraw-btn" onClick={() => setShowWithdraw(true)} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold">
                {t("withdraw")}
              </Button>
            </div>
          </div>

          {/* Earnings + withdrawals */}
          <div className="grid sm:grid-cols-2 gap-4 mb-5">
            <div className="p-4 rounded-2xl border border-border bg-card">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t("earnings")}</p>
              {(!wallet?.earnings || wallet.earnings.length === 0) ? (
                <p className="text-sm text-muted-foreground">{t("no_earnings")}</p>
              ) : wallet.earnings.slice(0, 5).map((e) => (
                <div key={e.id} className="flex justify-between text-sm py-1 border-b border-border/50 last:border-0">
                  <span className="truncate">{e.studio_name}</span>
                  <span className="text-emerald-400 font-medium">+{money(e.total_price, e.currency)}</span>
                </div>
              ))}
            </div>
            <div className="p-4 rounded-2xl border border-border bg-card">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t("withdrawals")}</p>
              {(!wallet?.withdrawals || wallet.withdrawals.length === 0) ? (
                <p className="text-sm text-muted-foreground">—</p>
              ) : wallet.withdrawals.map((w) => (
                <div key={w.id} className="flex justify-between text-sm py-1 border-b border-border/50 last:border-0">
                  <span>{t(w.method === "paypal" ? "paypal" : "bank")}</span>
                  <span className="text-muted-foreground">{money(w.amount)} · {w.status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly income */}
          <div className="p-4 rounded-2xl border border-border bg-card mb-5" data-testid="income-panel">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t("income_panel")}</p>
            {(!income || income.items.length === 0) ? (
              <p className="text-sm text-muted-foreground">{t("no_income")}</p>
            ) : (
              <div className="space-y-1.5">
                {income.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b border-border/50 last:border-0">
                    <span><span className="text-muted-foreground">{it.month}</span> · {it.studio_name} <span className="text-xs text-muted-foreground">({it.count})</span></span>
                    <span className="text-emerald-400 font-medium">{money(it.total)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-display font-bold pt-2 border-t border-border">
                  <span>{t("income_total")}</span><span className="text-primary">{money(income.grand_total)}</span>
                </div>
              </div>
            )}
          </div>

          {/* My studios */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-bold">{t("my_studios")}</h2>
            <Button data-testid="add-studio-btn" onClick={() => { setEditStudio(null); setShowCreate(true); }} size="sm" className="rounded-xl bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-1" /> {t("add_studio")}
            </Button>
          </div>
          <div className="space-y-3 mb-6">
            {studios.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8 border border-dashed border-border rounded-2xl">—</p>
            ) : studios.map((s) => (
              <div key={s.id} data-testid={`my-studio-${s.id}`} className="flex items-center gap-3 p-3 rounded-2xl border border-border bg-card">
                <img src={(s.photos && s.photos[0]) || PLACEHOLDER} alt="" className="w-16 h-16 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{s.name}</p>
                  <p className="text-xs text-muted-foreground">{s.city} · {money(s.price_per_hour, s.currency)}{t("per_hour")}</p>
                  <Badge variant="outline" className={`mt-1 ${vstatus[s.verification_status] || ""}`}>
                    {t(`status_${s.verification_status}`)}
                  </Badge>
                </div>
                <Button data-testid={`edit-studio-${s.id}`} onClick={() => { setEditStudio(s); setShowCreate(true); }} variant="ghost" size="icon" className="text-primary hover:bg-primary/10">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button data-testid={`delete-studio-${s.id}`} onClick={() => del(s.id)} variant="ghost" size="icon" className="text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          {/* Incoming bookings */}
          <h2 className="font-display text-xl font-bold mb-3">{t("owner_bookings")}</h2>
          <div className="space-y-2 mb-4" data-testid="owner-bookings">
            {ownerBookings.filter((b) => b.status !== "cancelled").length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded-2xl">{t("no_incoming")}</p>
            ) : ownerBookings.filter((b) => b.status !== "cancelled").map((b) => (
              <div key={b.id} data-testid={`owner-booking-${b.id}`} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card">
                <div className="flex flex-col items-center justify-center w-12 h-12 rounded-lg bg-primary/15 text-primary shrink-0">
                  <span className="text-[10px] uppercase">{new Date(b.date + "T00:00:00").toLocaleDateString(undefined, { month: "short" })}</span>
                  <span className="font-bold leading-none">{new Date(b.date + "T00:00:00").getDate()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{b.studio_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{t("booked_by")} {b.user_name} · {String(b.start_hour).padStart(2, "0")}:00 · {b.hours}h</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold text-emerald-400">{money(b.total_price, b.currency)}</p>
                  {b.payment_method === "cash" && (
                    b.payment_status === "collected"
                      ? <span className="text-[10px] text-emerald-400 font-semibold">✓ {t("collected")}</span>
                      : <button data-testid={`collect-${b.id}`} onClick={() => collect(b.id)} className="text-[10px] text-primary font-semibold hover:underline">{t("mark_collected")}</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {user?.is_admin && <AdminPanel onChange={load} />}

      <CreateStudioDialog open={showCreate} editStudio={editStudio} onClose={() => { setShowCreate(false); setEditStudio(null); }} onCreated={load} />
      <WithdrawDialog open={showWithdraw} onClose={() => setShowWithdraw(false)} max={wallet?.available || 0} onDone={load} />
    </div>
  );
}
